import { useGoogleLogin } from '@react-oauth/google';
import { useDispatch } from 'react-redux';
import { closeModal, showError } from '../../stores/slices/commonSlice';
import { loginWithGoogleAction } from '../../stores/actions/authActions';
import { isGoogleLoginConfigured, shouldUseGoogleRedirect, googleRedirectStartUrl } from '../../config/oauth';

const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true" className="h-5 w-5 shrink-0">
        <path
            fill="#FFC107"
            d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.223 36 24 36c-5.522 0-10-4.477-10-10s4.478-10 10-10c2.25 0 4.334.748 6.005 2.006l5.657-5.657C33.64 10.053 28.991 8 24 8c-8.837 0-16 7.163-16 16s7.163 16 16 16c8.837 0 16-7.163 16-16 0-1.061-.103-2.099-.389-3.083z"
        />
        <path
            fill="#FF3D00"
            d="M6.306 14.691l6.571 4.819C14.655 16.108 18.961 12 24 12c2.25 0 4.334.748 6.005 2.006l5.657-5.657C33.64 10.053 28.991 8 24 8c-6.458 0-11.994 3.693-14.694 9.691z"
        />
        <path
            fill="#4CAF50"
            d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
        />
        <path
            fill="#1976D2"
            d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.061-.103-2.099-.389-3.083z"
        />
    </svg>
);

const BUTTON_CLASS =
    'flex h-12 w-full items-center justify-center gap-2.5 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-800 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60';

function GoogleOAuthLoginButton() {
    const dispatch = useDispatch();
    const login = useGoogleLogin({
        onSuccess: (codeResponse) => {
            dispatch(loginWithGoogleAction({ access_token: codeResponse.access_token })).then((res) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    dispatch(closeModal());
                }
            });
        },
        onError: (error) => dispatch(showError(error?.error_description || 'Google login failed')),
    });

    return (
        <button type="button" onClick={() => login()} className={BUTTON_CLASS}>
            <GoogleIcon />
            Continue With Google
        </button>
    );
}

/**
 * Event short hosts (foammadnessja.apsession.com) cannot open the Google popup —
 * see shouldUseGoogleRedirect(). Leave the page for the apex-hosted redirect
 * flow; GoogleRedirectReturn finishes the login when the browser comes back.
 */
function GoogleRedirectLoginButton() {
    const start = () => {
        window.location.assign(googleRedirectStartUrl(window.location.href));
    };

    return (
        <button type="button" onClick={start} className={BUTTON_CLASS}>
            <GoogleIcon />
            Continue With Google
        </button>
    );
}

export default function LoginWithGoogleComponent() {
    const dispatch = useDispatch();

    if (shouldUseGoogleRedirect()) {
        return <GoogleRedirectLoginButton />;
    }

    if (!isGoogleLoginConfigured()) {
        if (import.meta.env.DEV) {
            console.warn(
                '[Apsession] Google sign-in button is visible but VITE_GOOGLE_CLIENT_ID is not set.'
            );
        }
        return (
            <button
                type="button"
                onClick={() =>
                    dispatch(
                        showError(
                            'Google sign-in is temporarily unavailable. Please use email and password.'
                        )
                    )
                }
                className={BUTTON_CLASS}
            >
                <GoogleIcon />
                Continue With Google
            </button>
        );
    }

    return <GoogleOAuthLoginButton />;
}
